import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Flame, Search, Trash2, KeyRound, Pencil, Users, Filter } from 'lucide-react';
import { AdminShell } from '@/components/admin-shell';
import { FgCard } from '@/components/fg-card';
import { FgButton } from '@/components/fg-button';
import { FgInput } from '@/components/fg-input';
import { FgToggle } from '@/components/fg-toggle';
import { FgToast } from '@/components/fg-toast';
import { FgDialog } from '@/components/fg-dialog';
import { AccentDot, Bento, PageHero, Pill, Widget } from '@/components/Widget';
import { api } from '@/lib/api';

/* ────────────────────────────────────────────────────────────────────────── */

interface User {
  id: string;
  email: string;
  fullName: string;
  phone: string | null;
  isAdmin: boolean;
  isActive: boolean;
  oauthProvider: 'google' | 'apple' | 'dev';
  revenuecatId: string | null;
  createdAt: string;
  deletedAt: string | null;
}

interface Level {
  id: string;
  name: string;
  displayName: string;
}

interface Tier {
  id: string;
  name: string;
  priceCents: number;
  currency: string;
  monthsValid: number;
  monthsRevision: number;
  description: string | null;
}

interface UserStreak {
  currentStreak: number;
  longestStreak: number;
  lastActiveDate: string | null;
}

/* ────────────────────────────────────────────────────────────────────────── */

export function LearnersPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [grantFor, setGrantFor] = useState<User | null>(null);

  const users = useQuery<User[]>({
    queryKey: ['admin', 'users', search, includeDeleted],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.set('q', search);
      const list = await api.get<User[]>(`/api/admin/users?${params.toString()}`);
      return includeDeleted ? list.data : list.data.filter((u) => !u.deletedAt);
    },
  });

  const remove = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/api/admin/users/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      FgToast.success('User soft-deleted');
    },
    onError: (e) => FgToast.error((e as { message?: string }).message ?? 'Delete failed'),
  });

  return (
    <AdminShell>
      <Bento>
        <PageHero
          accent="blue"
          eyebrow={
            <span className="inline-flex items-center gap-1.5">
              <Users size={11} /> People
            </span>
          }
          title="Learners"
          description="Search, edit, grant access, or soft-delete any account."
          aside={
            <Pill accent="blue">
              {users.data?.length ?? "…"} accounts
            </Pill>
          }
        />

        <Widget accent="blue" fullWidth
          eyebrow="Filter"
          title="Search & scope"
          subtitle="By email or name. Toggle deleted to include soft-deleted accounts."
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr,auto]">
            <div className="relative">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-white/45"
              />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by email or name"
                className="h-12 w-full rounded-2xl border border-white/10 bg-[#0A0A0A]/60 pl-9 pr-3 text-sm text-white placeholder:text-white/40 focus:border-[#0A84FF] focus:outline-none"
              />
            </div>
            <label className="inline-flex items-center gap-2 self-center">
              <input
                type="checkbox"
                checked={includeDeleted}
                onChange={(e) => setIncludeDeleted(e.target.checked)}
                className="h-4 w-4 accent-[#0A84FF]"
              />
              <span className="text-sm text-white/85 inline-flex items-center gap-1.5">
                <Filter size={12} /> Show deleted
              </span>
            </label>
          </div>
        </Widget>

        <div className="fg-span-4 flex flex-col gap-2">
          {users.data?.map((u) => (
            <FgCard key={u.id} className="flex items-center justify-between p-3 fg-anim-fast">
              <div className="flex items-center gap-3">
                <AccentDot accent="blue" size="sm">
                  <span className="text-[11px] font-black">{initials(u.fullName)}</span>
                </AccentDot>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-white">{u.fullName}</span>
                    {u.isAdmin && (
                      <span className="rounded bg-[#FF453A]/20 px-1.5 py-0.5 text-[10px] font-bold text-[#FF8A93]">
                        ADMIN
                      </span>
                    )}
                    {!u.isActive && (
                      <span className="rounded bg-white/10 px-1.5 py-0.5 text-[10px] font-bold text-white/55">
                        DISABLED
                      </span>
                    )}
                    {u.deletedAt && (
                      <span className="rounded bg-white/5 px-1.5 py-0.5 text-[10px] font-bold text-white/55">
                        DELETED
                      </span>
                    )}
                    <StreakChip userId={u.id} />
                  </div>
                  <p className="text-xs text-white/55">{u.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <FgButton size="sm" variant="outline" onClick={() => setGrantFor(u)}>
                  <KeyRound size={14} /> Grant access
                </FgButton>
                <FgButton size="sm" variant="ghost" onClick={() => setDetailId(u.id)}>
                  View
                </FgButton>
                {!u.deletedAt && (
                  <FgButton
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      if (confirm(`Soft-delete ${u.email}?`)) remove.mutate(u.id);
                    }}
                  >
                    <Trash2 size={14} />
                  </FgButton>
                )}
              </div>
            </FgCard>
          ))}
          {users.data?.length === 0 && (
            <p className="rounded-2xl bg-[#0A0A0A] p-6 text-center text-sm text-white/45">
              No learners match your search.
            </p>
          )}
        </div>
      </Bento>

      {detailId && <UserDetail id={detailId} onClose={() => setDetailId(null)} />}
      {grantFor && (
        <GrantAccessDialog
          user={grantFor}
          onClose={() => setGrantFor(null)}
          onGranted={() => {
            qc.invalidateQueries({ queryKey: ['admin', 'users'] });
          }}
        />
      )}
    </AdminShell>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

function StreakChip({ userId }: { userId: string }) {
  const q = useQuery<UserStreak>({
    queryKey: ['admin', 'users', userId, 'streak'],
    queryFn: async () => (await api.get(`/api/admin/users/${userId}/streak`)).data,
    staleTime: 60_000,
  });
  const current = q.data?.currentStreak ?? 0;
  const tone =
    current === 0
      ? 'bg-white/5 text-white/45'
      : current >= 7
        ? 'bg-[#FF453A]/15 text-[#FF8A93]'
        : 'bg-[#FF9F0A]/15 text-[#FFB43D]';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${tone}`}
      title={q.data ? `Longest: ${q.data.longestStreak} days` : 'No streak yet'}
    >
      <Flame size={10} /> {current}d
    </span>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */

function UserEditDialog({
  user,
  onClose,
  onSaved,
}: {
  user: User;
  onClose: () => void;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  const [fullName, setFullName] = useState(user.fullName);
  const [phone, setPhone] = useState(user.phone ?? "");
  const [isAdmin, setIsAdmin] = useState(user.isAdmin);
  const [isActive, setIsActive] = useState(user.isActive);

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        fullName: fullName.trim(),
        phone: phone.trim() || null,
        isAdmin,
        isActive,
      };
      return (await api.patch(`/api/admin/users/${user.id}`, body)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      qc.invalidateQueries({ queryKey: ['admin', 'users', user.id] });
      FgToast.success("User updated");
      onSaved();
    },
    onError: (e) =>
      FgToast.error((e as { message?: string }).message ?? "Update failed"),
  });

  return (
    <FgDialog
      open
      title={`Edit ${user.email}`}
      size="md"
      onClose={onClose}
      footer={
        <>
          <FgButton variant="ghost" onClick={onClose}>
            Cancel
          </FgButton>
          <FgButton
            variant="primary"
            onClick={() => save.mutate()}
            loading={save.isPending}
          >
            Save changes
          </FgButton>
        </>
      }
    >
      <div className="space-y-3">
        <FgInput
          label="Full name"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
        />
        <FgInput
          label="Phone"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          hint="Optional — used for booking SMS reminders."
        />
        <div className="flex items-center justify-between rounded-lg border border-white/5 bg-white/[0.02] p-3">
          <div>
            <p className="text-sm font-semibold text-[#FAFAFA]">Admin access</p>
            <p className="text-[11px] text-[#8E8E93]">
              Admins can manage tiers, lessons, exams, and other learners.
            </p>
          </div>
          <FgToggle checked={isAdmin} onChange={setIsAdmin} />
        </div>
        <div className="flex items-center justify-between rounded-lg border border-white/5 bg-white/[0.02] p-3">
          <div>
            <p className="text-sm font-semibold text-[#FAFAFA]">Active account</p>
            <p className="text-[11px] text-[#8E8E93]">
              Inactive accounts can't sign in but keep their lesson history.
            </p>
          </div>
          <FgToggle checked={isActive} onChange={setIsActive} />
        </div>
      </div>
    </FgDialog>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */

function UserResetPasswordDialog({
  user,
  onClose,
}: {
  user: User;
  onClose: () => void;
}) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  const reset = useMutation({
    mutationFn: async () =>
      (
        await api.patch(`/api/admin/users/${user.id}/password`, {
          password,
        })
      ).data,
    onSuccess: () => {
      FgToast.success(
        `Password reset for ${user.email}. They can sign in immediately.`,
      );
      onClose();
    },
    onError: (e) =>
      FgToast.error((e as { message?: string }).message ?? "Reset failed"),
  });

  const tooShort = password.length < 8;
  const mismatch = confirm !== password;

  return (
    <FgDialog
      open
      title={`Reset password — ${user.email}`}
      size="sm"
      tone="danger"
      onClose={onClose}
      footer={
        <>
          <FgButton variant="ghost" onClick={onClose}>
            Cancel
          </FgButton>
          <FgButton
            variant="destructive"
            onClick={() => reset.mutate()}
            loading={reset.isPending}
            disabled={tooShort || mismatch}
          >
            Reset password
          </FgButton>
        </>
      }
    >
      <div className="space-y-3">
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-200">
          The user can sign in with the new password immediately.
          Existing sessions stay valid until they expire — force-revoke
          is a separate operation.
        </div>
        <FgInput
          label="New password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          hint="Min 8 characters."
          autoComplete="new-password"
        />
        <FgInput
          label="Confirm password"
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          autoComplete="new-password"
          hint={mismatch ? "Passwords don't match." : " "}
        />
      </div>
    </FgDialog>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */

function UserDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const user = useQuery<User>({
    queryKey: ['admin', 'users', id],
    queryFn: async () => (await api.get(`/api/admin/users/${id}`)).data,
  });
  const [editing, setEditing] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);

  return (
    <FgCard className="mt-6 p-5">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold">Learner detail</h2>
        <div className="flex items-center gap-2">
          {user.data && (
            <>
              <FgButton
                size="sm"
                variant="outline"
                onClick={() => setEditing(true)}
                leftIcon={<Pencil className="w-3.5 h-3.5" />}
              >
                Edit
              </FgButton>
              <FgButton
                size="sm"
                variant="outline"
                onClick={() => setResettingPassword(true)}
                leftIcon={<KeyRound className="w-3.5 h-3.5" />}
              >
                Reset password
              </FgButton>
            </>
          )}
          <button
            onClick={onClose}
            className="text-xs text-[#8E8E93] hover:text-gray-700"
          >
            Close
          </button>
        </div>
      </div>
      {user.isLoading && <p className="mt-4 text-sm text-gray-500">Loading…</p>}
      {user.data && (
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <KV label="ID" value={user.data.id} />
          <KV label="Email" value={user.data.email} />
          <KV label="Name" value={user.data.fullName} />
          <KV label="Phone" value={user.data.phone ?? '—'} />
          <KV label="Provider" value={user.data.oauthProvider} />
          <KV label="RevenueCat ID" value={user.data.revenuecatId ?? '—'} />
          <KV label="Admin" value={user.data.isAdmin ? 'yes' : 'no'} />
          <KV label="Active" value={user.data.isActive ? 'yes' : 'no'} />
          <KV label="Created" value={new Date(user.data.createdAt).toLocaleString()} />
          {user.data.deletedAt && (
            <KV label="Deleted" value={new Date(user.data.deletedAt).toLocaleString()} />
          )}
        </div>
      )}
      {editing && user.data && (
        <UserEditDialog
          user={user.data}
          onClose={() => setEditing(false)}
          onSaved={() => setEditing(false)}
        />
      )}
      {resettingPassword && user.data && (
        <UserResetPasswordDialog
          user={user.data}
          onClose={() => setResettingPassword(false)}
        />
      )}
    </FgCard>
  );
}

function KV({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-1 text-sm text-black">{value}</div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */

function GrantAccessDialog({
  user,
  onClose,
  onGranted,
}: {
  user: User;
  onClose: () => void;
  onGranted: () => void;
}) {
  const levels = useQuery<Level[]>({
    queryKey: ['levels'],
    queryFn: async () => (await api.get('/api/levels')).data,
  });
  const tiers = useQuery<Tier[]>({
    queryKey: ['tiers'],
    queryFn: async () => (await api.get('/api/subscriptions/tiers')).data,
  });
  const [levelId, setLevelId] = useState('');
  const [tierId, setTierId] = useState('');
  const [daysValid, setDaysValid] = useState('60');

  useState(() => {
    if (!levelId && levels.data?.[0]) setLevelId(levels.data[0].id);
    if (!tierId && tiers.data?.[0]) setTierId(tiers.data[0].id);
  });

  const grant = useMutation({
    mutationFn: async () =>
      (
        await api.post('/api/subscriptions/grant', {
          userId: user.id,
          levelId,
          tierId,
          daysValid: Number(daysValid) || 60,
        })
      ).data,
    onSuccess: () => {
      FgToast.success(`Granted ${daysValid}-day access to ${user.email}`);
      onGranted();
      onClose();
    },
    onError: (e) => FgToast.error((e as { message?: string }).message ?? 'Grant failed'),
  });

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <FgCard className="w-full max-w-md p-5 shadow-fg-3">
        <h2 className="text-base font-semibold">Grant access</h2>
        <p className="mt-1 text-xs text-gray-600">
          Manually unlock <span className="font-semibold">{user.email}</span> for a level. Use this
          to support learners whose RevenueCat purchase is broken, or for comp / promo accounts.
        </p>
        <div className="mt-4 flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-700">Level</label>
            <select
              value={levelId}
              onChange={(e) => setLevelId(e.target.value)}
              className="h-12 rounded-md border border-[rgba(255,255,255,0.1)] bg-[#1C1C1E] px-3 text-sm focus:border-red-500 focus:outline-none"
            >
              {levels.data?.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name} · {l.displayName}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-700">Tier</label>
            <select
              value={tierId}
              onChange={(e) => setTierId(e.target.value)}
              className="h-12 rounded-md border border-[rgba(255,255,255,0.1)] bg-[#1C1C1E] px-3 text-sm focus:border-red-500 focus:outline-none"
            >
              {tiers.data?.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} · {(t.priceCents / 100).toFixed(2)} {t.currency}
                </option>
              ))}
            </select>
          </div>
          <FgInput
            label="Days valid"
            type="number"
            value={daysValid}
            onChange={(e) => setDaysValid(e.target.value)}
          />
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <FgButton variant="ghost" onClick={onClose}>
            Cancel
          </FgButton>
          <FgButton onClick={() => grant.mutate()} loading={grant.isPending}>
            Grant
          </FgButton>
        </div>
      </FgCard>
    </div>
  );
}